import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Play, RotateCcw, Heart, CheckCircle, XCircle, ChevronRight } from "lucide-react";
import { API, BACKEND_URL } from "../App";

export default function StoryPlayerPage() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro, playing, question, reveal, finished
  const [currentPointIndex, setCurrentPointIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const videoRef = useRef(null);
  const checkpointTriggered = useRef(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const wRes = await axios.get(`${API}/weddings/slug/${slug}`);
        setWedding(wRes.data);
        const sRes = await axios.get(`${API}/stories/public/${wRes.data.wedding_id}`);
        setStory(sRes.data);
      } catch {
        setError("No interactive story available yet.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const getMediaUrl = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  const points = story?.interaction_points || [];
  const currentPoint = points[currentPointIndex];
  const hasVideo = !!story?.video_url;

  // Video mode: pause at timestamps
  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current || phase !== "playing" || points.length === 0) return;
    const currentTime = videoRef.current.currentTime;
    for (let i = currentPointIndex; i < points.length; i++) {
      const ts = parseFloat(points[i].timestamp);
      if (currentTime >= ts && !checkpointTriggered.current.has(i)) {
        checkpointTriggered.current.add(i);
        setCurrentPointIndex(i);
        videoRef.current.pause();
        setPhase("question");
        setSelectedAnswer(null);
        setIsCorrect(null);
        break;
      }
    }
  }, [phase, points, currentPointIndex]);

  const handleStart = () => {
    checkpointTriggered.current.clear();
    setCurrentPointIndex(0);
    setScore(0);
    if (hasVideo) {
      setPhase("playing");
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
      }
    } else {
      // Quiz-only mode: go straight to first question
      setPhase("question");
    }
  };

  const handleAnswer = (option) => {
    setSelectedAnswer(option);
    const correct = option === currentPoint?.correct_answer;
    setIsCorrect(correct);
    if (correct) setScore(s => s + 1);
    setPhase("reveal");
  };

  const handleContinue = () => {
    const nextIdx = currentPointIndex + 1;
    if (hasVideo) {
      if (nextIdx < points.length) {
        setPhase("playing");
        videoRef.current?.play().catch(() => {});
      } else {
        setPhase("playing");
        videoRef.current?.play().catch(() => {});
      }
    } else {
      // Quiz-only: advance to next question or finish
      if (nextIdx < points.length) {
        setCurrentPointIndex(nextIdx);
        setSelectedAnswer(null);
        setIsCorrect(null);
        setPhase("question");
      } else {
        setPhase("finished");
      }
    }
  };

  const handleVideoEnd = () => setPhase("finished");

  const handleReplay = () => {
    checkpointTriggered.current.clear();
    setCurrentPointIndex(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setScore(0);
    setPhase("intro");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="w-16 h-16 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">{error || "Story not found"}</h1>
          <Link to={`/${slug}`} className="btn-secondary mt-6 inline-block">Back to Wedding</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wedding-black" data-testid="story-player-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-wedding-black/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}`} className="flex items-center gap-2 text-wedding-ivory/60 hover:text-wedding-gold transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </Link>
          <h1 className="font-serif text-lg text-wedding-gold">{story.title}</h1>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Content Area — Video or Quiz Card */}
        <div className={`relative ${hasVideo ? "aspect-video" : "min-h-[450px]"} bg-wedding-charcoal rounded-sm overflow-hidden mb-8`}>
          {hasVideo && (
            <video
              ref={videoRef}
              src={getMediaUrl(story.video_url)}
              className="w-full h-full object-contain bg-black"
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnd}
              data-testid="story-video-player"
            />
          )}

          {/* Non-video background */}
          {!hasVideo && phase !== "intro" && phase !== "finished" && (
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 30%, #1a1508 0%, #0A0A0A 70%)" }} />
          )}

          {/* Intro Overlay */}
          <AnimatePresence>
            {phase === "intro" && (
              <motion.div
                className="absolute inset-0 bg-wedding-black/90 flex items-center justify-center"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center px-8">
                  <Heart className="w-12 h-12 text-wedding-gold mx-auto mb-6" />
                  <h2 className="font-serif text-3xl text-wedding-ivory mb-4">{story.title}</h2>
                  <p className="text-wedding-ivory/60 mb-3 max-w-md mx-auto">
                    {hasVideo
                      ? "Watch and answer questions along the way."
                      : `Test how well you know this love story — ${points.length} question${points.length !== 1 ? "s" : ""} await.`
                    }
                  </p>
                  <p className="text-wedding-gold/60 text-sm mb-8">How well do you know this love story?</p>
                  <button
                    onClick={handleStart}
                    className="px-10 py-4 bg-wedding-gold text-wedding-black font-bold uppercase tracking-widest text-sm hover:bg-wedding-ivory transition-colors"
                    data-testid="story-start-btn"
                  >
                    <Play className="w-5 h-5 inline mr-2" />
                    {hasVideo ? "Start Experience" : "Start Quiz"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Question Overlay */}
          <AnimatePresence>
            {phase === "question" && currentPoint && (
              <motion.div
                className={`${hasVideo ? "absolute" : "absolute"} inset-0 ${hasVideo ? "bg-wedding-black/85" : ""} flex items-center justify-center`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center px-8 max-w-lg w-full">
                  <p className="text-wedding-gold text-sm uppercase tracking-widest mb-2">Question {currentPointIndex + 1} of {points.length}</p>
                  <h3 className="font-serif text-2xl text-wedding-ivory mb-8">{currentPoint.question}</h3>
                  <div className="space-y-3">
                    {(currentPoint.options || []).map((option, i) => (
                      <motion.button
                        key={i}
                        onClick={() => handleAnswer(option)}
                        className="w-full p-4 text-left bg-wedding-charcoal border border-white/10 hover:border-wedding-gold/50 hover:bg-wedding-gold/5 transition-all text-wedding-ivory"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.08 }}
                        data-testid={`story-option-${i}`}
                      >
                        <span className="text-wedding-gold mr-3 font-bold">{String.fromCharCode(65 + i)}.</span>
                        {option}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reveal Overlay */}
          <AnimatePresence>
            {phase === "reveal" && (
              <motion.div
                className={`absolute inset-0 ${hasVideo ? "bg-wedding-black/85" : ""} flex items-center justify-center`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center px-8">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
                    {isCorrect ? (
                      <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    ) : (
                      <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    )}
                  </motion.div>
                  <h3 className="font-serif text-2xl text-wedding-ivory mb-2">
                    {isCorrect ? "Correct!" : "Not quite!"}
                  </h3>
                  <p className="text-wedding-ivory/60 mb-2">
                    The answer is: <span className="text-wedding-gold font-semibold">{currentPoint?.correct_answer}</span>
                  </p>
                  <button
                    onClick={handleContinue}
                    className="mt-6 px-8 py-3 bg-wedding-gold text-wedding-black font-bold uppercase tracking-widest text-sm hover:bg-wedding-ivory transition-colors inline-flex items-center gap-2"
                    data-testid="story-continue-btn"
                  >
                    {currentPointIndex < points.length - 1 || hasVideo ? "Continue" : "See Results"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Finished Overlay */}
          <AnimatePresence>
            {phase === "finished" && (
              <motion.div
                className="absolute inset-0 bg-wedding-black/90 flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="text-center px-8">
                  <Heart className="w-16 h-16 text-wedding-gold mx-auto mb-4" />
                  <h2 className="font-serif text-3xl text-wedding-ivory mb-2">Experience Complete</h2>
                  {points.length > 0 && (
                    <p className="text-wedding-gold text-lg mb-2">
                      You scored {score} / {points.length}
                    </p>
                  )}
                  <p className="text-wedding-ivory/60 mb-8">Thank you for being part of their story</p>
                  <div className="flex gap-4 justify-center flex-wrap">
                    <button
                      onClick={handleReplay}
                      className="px-8 py-3 border border-wedding-gold/30 text-wedding-gold hover:bg-wedding-gold/5 uppercase tracking-widest text-sm transition-colors"
                      data-testid="story-replay-btn"
                    >
                      <RotateCcw className="w-4 h-4 inline mr-2" />
                      Replay
                    </button>
                    <Link
                      to={`/${slug}`}
                      className="px-8 py-3 bg-wedding-gold text-wedding-black font-bold uppercase tracking-widest text-sm hover:bg-wedding-ivory transition-colors inline-block"
                    >
                      Back to Wedding
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Progress */}
        {phase !== "intro" && points.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between text-wedding-ivory/40 text-xs mb-2">
              <span>Progress</span>
              <span>{Math.min(currentPointIndex + 1, points.length)} / {points.length} questions</span>
            </div>
            <div className="h-1 bg-wedding-charcoal rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-wedding-gold"
                animate={{ width: `${((currentPointIndex + (phase === "reveal" || phase === "finished" ? 1 : 0)) / points.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
